import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { guestAPI } from '../services/api';

const OrderStatus = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState('');

  const fetchOrderStatus = useCallback(async () => {
    try {
      const response = await guestAPI.getOrderStatus(orderId);
      setOrder(response.data);
      // Set email from order if exists
      if (response.data.notification_email) {
        setEmail(response.data.notification_email);
        setEmailSaved(true);
      }
      setError('');
    } catch (err) {
      setError('Order not found or invalid order ID');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderStatus();
    
    // Poll every 15 seconds if order is not in final state
    const interval = setInterval(() => {
      if (order && !['finished', 'completed', 'failed', 'expired'].includes(order.payment_status)) {
        fetchOrderStatus();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchOrderStatus, order]);

  const handleSaveEmail = async () => {
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailSaving(true);
    setEmailError('');
    
    try {
      await guestAPI.updateOrderEmail(orderId, email);
      setEmailSaved(true);
      setEmailError('');
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Failed to save email');
    } finally {
      setEmailSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
      case 'completed':
      case 'confirmed':
        return 'text-green-400 bg-green-500/20 border-green-500/50';
      case 'confirming':
      case 'sending':
      case 'processing':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      case 'waiting':
      case 'pending':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/50';
      case 'partially_paid':
        return 'text-orange-400 bg-orange-500/20 border-orange-500/50';
      case 'failed':
      case 'expired':
        return 'text-red-400 bg-red-500/20 border-red-500/50';
      default:
        return 'text-gray-400 bg-gray-500/20 border-gray-500/50';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'waiting': '⏱️ Waiting for Payment',
      'confirming': '⏳ Confirming on Blockchain',
      'confirmed': '✅ Payment Confirmed',
      'sending': '📤 Processing',
      'finished': '✅ Payment Complete',
      'partially_paid': '⚠️ Insufficient Payment',
      'failed': '❌ Payment Failed',
      'expired': '⏰ Payment Expired',
      'pending': '⏱️ Pending',
      'processing': '🔄 Processing',
      'completed': '✅ Completed'
    };
    return statusMap[status] || status;
  };

  // Get order status color (for order processing status)
  const getOrderStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-400 bg-green-500/20 border-green-500/50';
      case 'processing':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/50';
      case 'pending':
      case 'pending_verification':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      case 'expired':
      case 'cancelled':
        return 'text-red-400 bg-red-500/20 border-red-500/50';
      default:
        return 'text-gray-400 bg-gray-500/20 border-gray-500/50';
    }
  };

  // Get order status text (for order processing status)
  const getOrderStatusText = (status) => {
    const statusMap = {
      'pending': '⏱️ Pending',
      'pending_verification': '🔍 Pending Verification',
      'processing': '🔄 Processing Order',
      'completed': '✅ Completed',
      'expired': '⏰ Expired',
      'cancelled': '❌ Cancelled'
    };
    return statusMap[status?.toLowerCase()] || status || 'Unknown';
  };

  // Get order status description
  const getOrderStatusDescription = (status) => {
    const descriptions = {
      'pending': 'Waiting for payment confirmation',
      'pending_verification': 'Payment received, waiting for verification',
      'processing': 'Your reactions are being delivered',
      'completed': 'Order completed successfully!',
      'expired': 'Order expired due to payment timeout',
      'cancelled': 'Order has been cancelled'
    };
    return descriptions[status?.toLowerCase()] || '';
  };

  const getReactionEmoji = (type) => {
    const emojis = {
      'rocket': '🚀',
      'fire': '🔥',
      'poop': '💩',
      'redflag': '🚩',
      'favorite': '⭐'
    };
    return emojis[type?.toLowerCase()] || '✨';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-8 max-w-md w-full border border-red-500/30 text-center">
          <div className="text-5xl mb-4">❌</div>
          <h1 className="text-xl font-bold text-red-400 mb-4">Order Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-block mb-4">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Dexreaction
            </h1>
          </Link>
          <h2 className="text-lg text-gray-300">Order Status</h2>
        </div>

        {/* Order Card */}
        <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-6 border border-purple-500/30">
          {/* Order ID */}
          <div className="text-center mb-4">
            <p className="text-xs text-gray-500 mb-1">Order ID</p>
            <p className="font-mono text-sm text-purple-400 break-all">{order.id}</p>
          </div>

          {/* Status Cards - Order Status & Payment Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Order Status Card */}
            <div className={`rounded-xl p-4 border ${getOrderStatusColor(order.status)}`}>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Order Status</p>
                <p className="text-lg font-bold">{getOrderStatusText(order.status)}</p>
                <p className="text-xs mt-2 opacity-75">{getOrderStatusDescription(order.status)}</p>
              </div>
            </div>

            {/* Payment Status Card */}
            <div className={`rounded-xl p-4 border ${getStatusColor(order.payment_status)}`}>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Payment Status</p>
                <p className="text-lg font-bold">{getStatusText(order.payment_status)}</p>
                {order.payment_status === 'waiting' && (
                  <p className="text-xs mt-2 opacity-75">Please complete your payment</p>
                )}
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="bg-slate-700/30 rounded-xl p-4 mb-6">
            <p className="text-xs text-gray-400 mb-4 uppercase tracking-wider">Order Progress</p>
            <div className="flex items-center justify-between relative">
              {/* Progress Line */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-600"></div>
              <div 
                className="absolute top-4 left-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                style={{ 
                  width: order.status === 'completed' ? '100%' 
                       : order.status === 'processing' ? '66%' 
                       : ['pending', 'pending_verification'].includes(order.status) ? '33%' 
                       : '0%' 
                }}
              ></div>
              
              {/* Step 1: Pending */}
              <div className="flex flex-col items-center relative z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                  ['pending', 'pending_verification', 'processing', 'completed'].includes(order.status)
                    ? 'bg-purple-500 text-white'
                    : order.status === 'expired' || order.status === 'cancelled'
                    ? 'bg-red-500/50 text-red-300'
                    : 'bg-slate-600 text-gray-400'
                }`}>
                  {order.status === 'expired' || order.status === 'cancelled' ? '✕' : '1'}
                </div>
                <span className="text-xs text-gray-400 mt-2">Pending</span>
              </div>

              {/* Step 2: Processing */}
              <div className="flex flex-col items-center relative z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                  ['processing', 'completed'].includes(order.status)
                    ? 'bg-blue-500 text-white'
                    : order.status === 'expired' || order.status === 'cancelled'
                    ? 'bg-slate-700 text-gray-500'
                    : 'bg-slate-600 text-gray-400'
                }`}>
                  2
                </div>
                <span className="text-xs text-gray-400 mt-2">Processing</span>
              </div>

              {/* Step 3: Completed */}
              <div className="flex flex-col items-center relative z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                  order.status === 'completed'
                    ? 'bg-green-500 text-white'
                    : order.status === 'expired' || order.status === 'cancelled'
                    ? 'bg-slate-700 text-gray-500'
                    : 'bg-slate-600 text-gray-400'
                }`}>
                  {order.status === 'completed' ? '✓' : '3'}
                </div>
                <span className="text-xs text-gray-400 mt-2">Completed</span>
              </div>
            </div>
          </div>

          {/* QR Code - Show if payment is still waiting */}
          {order.payment_id && ['waiting', 'pending'].includes(order.payment_status) && (
            <div className="flex justify-center mb-6">
              <img
                src={guestAPI.getPaymentQR(order.payment_id)}
                alt="Payment QR Code"
                className="w-40 h-40 bg-white rounded-lg p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Payment Address - Important! */}
          {order.pay_address && (
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-400 mb-2">Payment Address ({order.pay_currency?.toUpperCase()})</p>
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="font-mono text-xs text-gray-200 break-all mb-2">
                  {order.pay_address}
                </p>
                <button
                  onClick={() => copyToClipboard(order.pay_address)}
                  className="text-xs text-purple-400 hover:text-purple-300"
                >
                  📋 Copy Address
                </button>
              </div>
            </div>
          )}

          {/* Amount to Pay */}
          {order.pay_amount > 0 && (
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-400 mb-2">Amount to Pay</p>
              <div className="flex items-center justify-between">
                <span className="font-mono text-lg text-purple-400">
                  {order.pay_amount} {order.pay_currency?.toUpperCase()}
                </span>
                <button
                  onClick={() => copyToClipboard(order.pay_amount?.toString())}
                  className="text-xs text-purple-400 hover:text-purple-300 px-2 py-1 bg-slate-800 rounded"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {/* Order Details */}
          <div className="space-y-3 mb-6">
            <h3 className="font-semibold text-gray-300 text-sm border-b border-slate-700 pb-2">Order Details</h3>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400 text-sm">Reaction</span>
              <span className="font-semibold">
                {getReactionEmoji(order.reaction_type)} {order.reaction_type?.toUpperCase()}
              </span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400 text-sm">Quantity</span>
              <span className="font-semibold">
                {order.quantity} reactions
              </span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400 text-sm">Total USD</span>
              <span className="font-semibold text-green-400">${order.total_usd?.toFixed(2)}</span>
            </div>

            {order.voucher_code && (
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400 text-sm">Voucher</span>
                <span className="font-semibold text-yellow-400">{order.voucher_code} (-${order.discount_amount?.toFixed(2)})</span>
              </div>
            )}

            {order.actually_paid > 0 && (
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400 text-sm">Amount Paid</span>
                <span className={`font-mono ${order.actually_paid >= order.pay_amount ? 'text-green-400' : 'text-orange-400'}`}>
                  {order.actually_paid} {order.pay_currency?.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* DexScreener Link */}
          {order.dexscreener_link && (
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-400 mb-2">DexScreener Link</p>
              <a 
                href={order.dexscreener_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-purple-400 hover:text-purple-300 break-all flex items-center gap-2"
              >
                <span>🔗</span>
                <span className="underline">{order.dexscreener_link}</span>
              </a>
            </div>
          )}

          {/* Email Notification (Optional) */}
          <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
            <p className="text-xs text-gray-400 mb-2">📧 Email Notification (Optional)</p>
            <p className="text-xs text-gray-500 mb-3">
              Enter your email to receive status updates for this order.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailSaved(false);
                  setEmailError('');
                }}
                placeholder="your@email.com"
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={emailSaving}
              />
              <button
                onClick={handleSaveEmail}
                disabled={emailSaving || emailSaved}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  emailSaved 
                    ? 'bg-green-600 text-white cursor-default' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {emailSaving ? '...' : emailSaved ? '✓ Saved' : 'Save'}
              </button>
            </div>
            {emailError && (
              <p className="text-xs text-red-400 mt-2">{emailError}</p>
            )}
            {emailSaved && !emailError && (
              <p className="text-xs text-green-400 mt-2">
                ✓ You will receive email updates for this order.
              </p>
            )}
          </div>

          {/* Timestamps */}
          <div className="space-y-2 text-xs text-gray-500 border-t border-slate-700 pt-4">
            {order.created_at && (
              <div className="flex justify-between">
                <span>Created:</span>
                <span>{new Date(order.created_at).toLocaleString()}</span>
              </div>
            )}
            {order.expiration_estimate_date && order.payment_status === 'waiting' && (
              <div className="flex justify-between">
                <span>Expires:</span>
                <span className="text-orange-400">{new Date(order.expiration_estimate_date).toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Partial Payment Warning */}
          {order.is_partially_paid && (
            <div className="mt-6 bg-orange-500/20 border border-orange-500/50 rounded-lg p-4">
              <h4 className="font-bold text-orange-400 mb-2">⚠️ Insufficient Payment</h4>
              <p className="text-sm text-gray-300 mb-2">
                The payment received is less than the required amount.
              </p>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-400">Required:</span>
                  <span className="font-mono">{order.pay_amount} {order.pay_currency?.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Received:</span>
                  <span className="text-orange-400 font-mono">{order.actually_paid || order.partial_amount_received} {order.pay_currency?.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Shortage:</span>
                  <span className="text-red-400 font-mono">
                    {(order.pay_amount - (order.actually_paid || order.partial_amount_received || 0)).toFixed(8)} {order.pay_currency?.toUpperCase()}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Admin has been notified. Send the remaining amount to the same address or wait for manual processing.
              </p>
            </div>
          )}

          {/* Auto refresh indicator */}
          {!['finished', 'completed', 'failed', 'expired'].includes(order.payment_status) && (
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                🔄 Status updates automatically every 15 seconds
              </p>
            </div>
          )}

          {/* Success Message */}
          {(order.payment_status === 'finished' || order.status === 'completed') && (
            <div className="mt-6 bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
              <div className="text-3xl mb-2">🎉</div>
              <h4 className="font-bold text-green-400">Payment Successful!</h4>
              <p className="text-sm text-gray-300 mt-2">
                Your order is being processed by admin.
              </p>
            </div>
          )}

          {/* Failed/Expired Message */}
          {(order.payment_status === 'failed' || order.payment_status === 'expired') && (
            <div className="mt-6 bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
              <div className="text-3xl mb-2">{order.payment_status === 'expired' ? '⏰' : '❌'}</div>
              <h4 className="font-bold text-red-400">
                {order.payment_status === 'expired' ? 'Payment Expired' : 'Payment Failed'}
              </h4>
              <p className="text-sm text-gray-300 mt-2">
                Please create a new order if you want to continue.
              </p>
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm"
          >
            <span>←</span>
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderStatus;
